import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";

import { acceptTerms, fetchTerms, termsPdfUrl, type TermsResponse } from "../../api/client";
import { useCurrentUser } from "../../auth/useCurrentUser";
import { OnboardingBackButton, OnboardingShell } from "../../components/OnboardingShell";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { FormError } from "@/components/ui/form-error";

// Lazy + route-split so pdf.js, its worker, and the ~0.5 MB document download
// only when the user opens the terms modal — never in the main bundle, and not
// even on the step until they click through.
const PdfTermsViewer = lazy(() => import("@/components/PdfTermsViewer"));

/**
 * Step 3 of the post-invite onboarding wizard — Terms & Conditions.
 *
 * CHP-1245: the T&Cs are served as a PDF (ADR-0048) and shown in a large modal
 * (the step is one of a 35-page legal document; an inline box was unusable).
 * The read-gate + attestation + accept all live in the modal footer.
 *
 * Same backend call as the legacy /accept-terms route. ProtectedRoute redirects
 * users with tc_accepted_at == null to /onboarding/identity (the first step);
 * the legacy /accept-terms URL is preserved as a permanent redirect to here so
 * email links from earlier flows still resolve.
 */
export function OnboardingTermsStep() {
  const me = useCurrentUser();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [agreed, setAgreed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  // CHP-248 / CHP-1192 / CHP-1245: the partner must view the full T&Cs document
  // before accepting. `scrolledToBottom` latches true once the PDF has been
  // scrolled to the end (or the whole document fits without scrolling) and never
  // re-arms — scrolling back up doesn't re-close it.
  //
  // The scroll requirement is a HARD gate (UAT AC-A-07): the checkbox is
  // `disabled={!scrolledToBottom}`. CHP-809 warned that a single measurement
  // miss could leave the checkbox permanently disabled; that dead-end is
  // defended by the re-measure machinery below (ResizeObserver + the PDF render
  // callback + the 4px tolerance), NOT by softening the gate. `allRenderedRef`
  // gates all of it: the latch can only open once EVERY page has PAINTED —
  // measuring while lower pages of a multi-page document are still rendering
  // would let a premature "bottom" satisfy the gate. So a still-loading or
  // failed-to-load viewer can never satisfy it.
  const [scrolledToBottom, setScrolledToBottom] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const termsBodyRef = useRef<HTMLDivElement>(null);
  const allRenderedRef = useRef(false);

  const terms = useQuery<TermsResponse>({
    queryKey: ["legal", "terms"],
    queryFn: fetchTerms,
    staleTime: 5 * 60 * 1000,
  });

  // A document that fits without a scrollbar has nothing to scroll, so the gate
  // opens as soon as every page has rendered. Guarded by allRenderedRef so it
  // measures the fully-painted document, never a still-rendering one. Latches
  // once, so re-checks only ever open the gate.
  const latchIfFits = useCallback(() => {
    const el = termsBodyRef.current;
    if (!el || !allRenderedRef.current) return;
    if (el.scrollHeight <= el.clientHeight + 4) {
      setScrolledToBottom(true);
    }
  }, []);

  // Fired once every PDF page has painted (real, final geometry now exists).
  const handleAllRendered = useCallback(() => {
    allRenderedRef.current = true;
    latchIfFits();
  }, [latchIfFits]);

  // CHP-809 safety net: re-check the fit on layout changes (ResizeObserver) and
  // after web fonts settle. Re-runs when the modal opens (deps include
  // modalOpen) so the observer attaches to the now-mounted scroll container.
  useEffect(() => {
    const el = termsBodyRef.current;
    if (!el) return;

    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(latchIfFits) : null;
    observer?.observe(el);

    // `document.fonts` is absent in some test environments, so guard it.
    document.fonts?.ready.then(latchIfFits).catch(() => {});

    return () => observer?.disconnect();
  }, [latchIfFits, modalOpen]);

  // CHP-1154: this page doubles as the version-bump re-acceptance step.
  const isReacceptance = me.data?.tc_reaccept_required === true;

  const accept = useMutation({
    mutationFn: () => acceptTerms(me.accessToken!),
    onSuccess: async () => {
      // refetchQueries (NOT invalidateQueries) so the cache holds the
      // fresh `tc_accepted_at` BEFORE we navigate. invalidateQueries
      // marks stale but doesn't await — under load, ProtectedRoute
      // would render against the still-stale cache and bounce us back
      // to /onboarding/identity.
      //
      // CHP-808: this 2-segment key is a PREFIX of useCurrentUser's full
      // 3-segment key (["auth","me",userKey] — useCurrentUser.ts). TanStack
      // Query's queryKey filter is prefix-match by default, so this refetches
      // the active /auth/me query regardless of userKey — which is exactly the
      // intent (we don't have the sub here, and the user can only have one
      // active /auth/me query). If a future refactor switches this to
      // `exact: true`, it MUST also pass the userKey, or this post-accept
      // refetch silently no-ops and the navigation race above returns.
      await queryClient.refetchQueries({ queryKey: ["auth", "me"] });
      // CHP-1154: a version-bump re-acceptance is not a first onboarding —
      // skip the Welcome screen and return to the dashboard.
      if (isReacceptance) {
        void navigate({ to: "/dashboard" });
        return;
      }
      // POs land on the Registration Complete / Welcome screen (Flow A step
      // 6, CHP-207) with the invite-BDMs shortcut; BDMs/admins/managers go
      // straight to the dashboard. Mirrors the PO check used for the SPIF
      // step (channel_partner with is_org_admin !== false).
      const isPo = me.data?.role === "channel_partner" && me.data.is_org_admin !== false;
      void navigate({ to: isPo ? "/onboarding/complete" : "/dashboard" });
    },
    onError: () => setError("Couldn't record your acceptance. Try again."),
  });

  // CHP-1105: resolve the guard redirect as a value + fire it from a ref-guarded
  // effect instead of returning a render-time <Navigate>, which flashes the
  // wrong frame and can re-navigate on every re-render (the CHP-1203 loop).
  // `null` means "stay on this step". Decision order preserved exactly from the
  // sequential guards this replaced:
  //   - CHP-1154: an acceptance under an OLDER terms version does NOT bounce to
  //     the dashboard — this page doubles as the re-acceptance step (the
  //     dashboard's ProtectedRoute would only send them straight back here).
  //   - CHP-1021: a non-signing partner (a BDM, or an additional PO of an
  //     already-registered org) has no T&C step; the founding PO signed for the
  //     org. If one URL-hops here before finishing onboarding (tc still null),
  //     send them back to their single Identity step rather than letting them
  //     sign — submitting it stamps the org-inherited acceptance and the tc-set
  //     guard above then takes over. First-onboarding only (tc null): a version-
  //     stale non-signer (CHP-1154) re-accepts individually here; bouncing them
  //     to Identity would loop, as their identity step is long done.
  const redirectTarget: string | null =
    me.isAuthLoading || me.isLoading
      ? null
      : !me.isAuthenticated
        ? "/login"
        : me.data?.tc_accepted_at != null && !isReacceptance
          ? "/dashboard"
          : me.data?.tc_accepted_at == null &&
              me.data?.role === "channel_partner" &&
              (me.data.is_org_admin === false || me.data.org_already_registered === true)
            ? "/onboarding/identity"
            : null;

  const redirected = useRef(false);
  useEffect(() => {
    if (redirectTarget === null || redirected.current) return;
    redirected.current = true;
    void navigate({ to: redirectTarget, replace: true });
  }, [redirectTarget, navigate]);

  if (me.isAuthLoading || me.isLoading) {
    return <p role="status">Loading…</p>;
  }
  // Redirect pending → loader, never a flash of the wrong route.
  if (redirectTarget !== null) {
    return <p role="status">Loading…</p>;
  }

  // Only two populations reach this step now: the org's signing PO and
  // ResetData staff (CHP-1021's guard above redirects the non-signing partner
  // cohort to identity). For the signing PO, T&C is step 3 of 3 (after Identity
  // + SPIF); for admin/manager it's step 2 of 2 (SPIF skipped per Flow A).
  // `seesSpifStep` therefore reduces to "this is the signing PO" — a
  // channel_partner who is an org admin of a not-yet-registered org;
  // `is_org_admin !== false` defaults null/true to PO semantics for back-compat.
  const seesSpifStep =
    me.data?.role === "channel_partner" &&
    me.data.is_org_admin !== false &&
    me.data.org_already_registered !== true;
  // CHP-808: build the (totalSteps, step) pair as one discriminated value so the
  // OnboardingShell union accepts it — Terms is always the LAST step, so
  // step === totalSteps (3 for the SPIF path, 2 otherwise).
  const shell = seesSpifStep
    ? ({ totalSteps: 3, step: 3 } as const)
    : ({ totalSteps: 2, step: 2 } as const);

  if (terms.isLoading) {
    return (
      <OnboardingShell {...shell} title="Terms & Conditions">
        <p role="status">Loading terms…</p>
      </OnboardingShell>
    );
  }
  if (terms.error || !terms.data) {
    return (
      <OnboardingShell {...shell} title="Terms & Conditions">
        <p role="alert">Couldn't load the terms. Refresh the page.</p>
      </OnboardingShell>
    );
  }

  return (
    <OnboardingShell {...shell} title="Terms & Conditions">
      <p className="text-xs text-muted-foreground">Version: {terms.data.version}</p>
      <p className="text-sm text-muted-foreground">
        Open the ResetData Partner Program Terms &amp; Conditions, read to the end, and accept to
        continue.
      </p>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogTrigger asChild>
          <Button type="button" size="lg" className="w-full">
            View Terms &amp; Conditions
          </Button>
        </DialogTrigger>
        <DialogContent className="flex max-h-[90vh] w-full max-w-4xl flex-col gap-4">
          <DialogHeader>
            <DialogTitle>Terms &amp; Conditions</DialogTitle>
            <DialogDescription>
              Version {terms.data.version} — scroll to the end of the document to accept.
            </DialogDescription>
          </DialogHeader>

          <div
            ref={termsBodyRef}
            aria-label="Terms and Conditions document"
            className="min-h-0 flex-1 overflow-auto rounded-md border bg-muted/30 p-4"
            onScroll={(e) => {
              if (!allRenderedRef.current) return;
              const el = e.currentTarget;
              if (el.scrollTop + el.clientHeight >= el.scrollHeight - 4) {
                setScrolledToBottom(true);
              }
            }}
          >
            <Suspense fallback={<p role="status">Loading terms…</p>}>
              <PdfTermsViewer
                fileUrl={termsPdfUrl()}
                onAllRendered={handleAllRendered}
                onError={() => setPdfError(true)}
              />
            </Suspense>
          </div>

          {pdfError && (
            <p role="alert" className="text-xs text-destructive">
              Couldn&apos;t load the terms document. Close this dialog and refresh the page to try
              again.
            </p>
          )}

          <DialogFooter className="flex-col gap-3 sm:flex-col sm:items-stretch sm:space-x-0">
            <label className="flex items-start gap-2 text-sm">
              <input
                type="checkbox"
                checked={agreed}
                // CHP-1192: disabled until the PDF has been scrolled to the end
                // (hard gate per UAT AC-A-07). Latching means scrolling back up
                // never re-disables it. CHP-1245: also disabled on a load/render
                // error — you can't accept terms that failed to display.
                disabled={!scrolledToBottom || pdfError}
                aria-describedby={!scrolledToBottom ? "terms-scroll-hint" : undefined}
                onChange={(e) => {
                  setAgreed(e.target.checked);
                  setError(null);
                }}
                className="mt-1"
              />
              {/* CHP-809: the spec mandates an affirmative attestation ("I have
                  reviewed and agree…"), not a bare "Accept" — the "have read &
                  understood" clause is the legally load-bearing part. */}
              <span>
                I have read, understood, and accept the ResetData Partner Program Terms &amp;
                Conditions.
              </span>
            </label>

            {/* Persistent live region so flipping from disabled→enabled (the hint
                disappearing) is announced to screen readers (CHP-805). */}
            <div aria-live="polite">
              {!scrolledToBottom && (
                <p id="terms-scroll-hint" className="text-xs text-muted-foreground">
                  Please scroll through the full terms before accepting.
                </p>
              )}
            </div>

            {error && <FormError>{error}</FormError>}

            <Button
              type="button"
              disabled={!agreed}
              loading={accept.isPending}
              loadingLabel="Saving…"
              size="lg"
              onClick={() => accept.mutate()}
            >
              Complete registration
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* CHP-313: an explicit Back control to the previous wizard step. */}
      <OnboardingBackButton {...shell} />
    </OnboardingShell>
  );
}
